// Import per Drag & Drop oder Dateiauswahl: Audio (WAV, MP3, AIFF, FLAC, M4A, OGG) und MIDI-Dateien.
// Audio wird dekodiert, die Waveform sofort berechnet und die Originaldatei im Hintergrund hochgeladen,
// damit die anderen im Raum sie auch hören. MIDI wird in Noten (Beats) umgewandelt und folgt dem Songtempo.
import { barAtSec, beatAt, beatToBar, bpmAt, parseMidi, secOfBar, snapSec, type Clip, type Note, type Track } from '@srs/shared';
import { useStudio } from '../state/store.ts';
import { engine } from './engine.ts';
import { api } from '../api/client.ts';
import { emptyTrack, uid } from '../state/demo.ts';
import { toast } from '../components/ui.tsx';

const AUDIO = /\.(wav|wave|mp3|aif|aiff|flac|m4a|aac|ogg|oga|opus)$/i;
const MIDI = /\.(mid|midi|smf|kar)$/i;
const MIME: Record<string, string> = { wav: 'audio/wav', wave: 'audio/wav', mp3: 'audio/mpeg', aif: 'audio/aiff', aiff: 'audio/aiff', flac: 'audio/flac', m4a: 'audio/mp4', aac: 'audio/aac', ogg: 'audio/ogg', oga: 'audio/ogg', opus: 'audio/ogg' };
export const ACCEPT = '.wav,.mp3,.aif,.aiff,.flac,.m4a,.aac,.ogg,.mid,.midi';
export const TRACK_COLORS = ['#003399', '#2f5fd0', '#0f8b8d', '#6b4fbb', '#c7861b', '#e23e2c', '#1f8a5a', '#b5537a'];

export const isMidiFile = (f: File) => MIDI.test(f.name) || /midi/.test(f.type);
export const isAudioFile = (f: File) => AUDIO.test(f.name) || (f.type.startsWith('audio/') && !isMidiFile(f));
export const isImportable = (f: File) => isMidiFile(f) || isAudioFile(f);
const baseName = (n: string) => n.replace(/\.[^.]+$/, '').slice(0, 60) || 'Import';
const mimeOf = (f: File) => MIME[f.name.split('.').pop()?.toLowerCase() ?? ''] ?? (f.type || 'application/octet-stream');

/** Ziel des Imports: Zeitpunkt in Sekunden und (optional) die Spur, auf die gezogen wurde */
export interface DropTarget { time: number; trackId?: string | null }

/**
 * Dateien importieren. Mehrere Dateien landen untereinander auf eigenen Spuren.
 * Die erste Datei kommt auf die Zielspur, wenn sie zur Art passt (Audio auf Audio, MIDI auf MIDI).
 * Alles zusammen ist ein Schritt für Rückgängig.
 */
export async function importFiles(files: File[], target: DropTarget): Promise<string[]> {
  const list = files.filter(isImportable);
  const skipped = files.length - list.length;
  if (!list.length) { toast(skipped ? 'Nur WAV, MP3, AIFF, FLAC, M4A, OGG und MIDI-Dateien lassen sich importieren' : 'Keine Datei'); return []; }
  toast(list.length === 1 ? `Importiere ${list[0].name} …` : `Importiere ${list.length} Dateien …`);

  const st = useStudio.getState();
  const p0 = st.project;
  const at = new Date().toISOString();
  const by = { userId: st.user.id, at };

  type Prepared =
    | { kind: 'audio'; file: File; sourceId: string; duration: number }
    | { kind: 'midi'; file: File; parts: { name: string; notes: Note[]; drums: boolean; lengthBeats: number }[]; bpm?: number };
  const prepared: Prepared[] = [];
  const errors: string[] = [];

  for (const file of list) {
    try {
      const data = await file.arrayBuffer();
      if (isMidiFile(file)) {
        const m = parseMidi(data);
        if (!m.notes.length) { errors.push(`${file.name}: keine Noten`); continue; }
        // Nach Spur und Kanal gruppieren (Format 1 hat oft eine Spur pro Instrument)
        const groups = new Map<string, typeof m.notes>();
        for (const n of m.notes) { const k = `${n.track}:${n.channel}`; groups.set(k, [...(groups.get(k) ?? []), n]); }
        const ordered = [...groups.entries()].sort(([a], [b]) => { const [ta, ca] = a.split(':').map(Number), [tb, cb] = b.split(':').map(Number); return ta - tb || ca - cb; });
        const parts = ordered.map(([k, ns], i) => {
          const [tr, ch] = k.split(':').map(Number);
          const end = Math.max(...ns.map((n) => n.t + n.d));
          const name = m.trackNames[tr] || (groups.size > 1 ? `${baseName(file.name)} ${i + 1}` : baseName(file.name));
          return {
            name, drums: ch === 9,
            lengthBeats: end,
            notes: ns.map((n) => ({ t: round(n.t), d: round(n.d), m: n.m, v: Math.max(0.05, Math.min(1, n.v)), by })),
          };
        });
        prepared.push({ kind: 'midi', file, parts, bpm: m.bpm });
      } else {
        const sourceId = uid('src');
        const buf = await engine.decode(sourceId, data);
        prepared.push({ kind: 'audio', file, sourceId, duration: buf.duration });
      }
    } catch (e) {
      errors.push(`${file.name}: ${isMidiFile(file) ? 'MIDI-Datei nicht lesbar' : 'Audioformat wird vom Browser nicht unterstützt'}`);
      console.warn('Import', file.name, e);
    }
  }
  if (!prepared.length) { toast(errors.join(' · ')); return []; }

  const newClipIds: string[] = [];
  let firstTrack: string | null = null;
  useStudio.getState().commit(prepared.length === 1 ? `Import ${prepared[0].file.name}` : `Import ${prepared.length} Dateien`, (p) => {
    let insertAt = target.trackId ? p.tracks.findIndex((t) => t.id === target.trackId) + 1 : p.tracks.length;
    let usedTarget = false;
    const trackFor = (kind: 'audio' | 'midi', name: string, extra: Partial<Track>): Track => {
      const tgt = !usedTarget && target.trackId ? p.tracks.find((t) => t.id === target.trackId) : undefined;
      usedTarget = true;
      if (tgt && tgt.kind === kind) return tgt;
      const t = emptyTrack(name, kind, TRACK_COLORS[p.tracks.length % TRACK_COLORS.length], extra);
      p.tracks.splice(Math.min(insertAt, p.tracks.length), 0, t);
      insertAt = p.tracks.indexOf(t) + 1;
      return t;
    };
    for (const it of prepared) {
      if (it.kind === 'audio') {
        const start = Math.max(0, snapSec(p, target.time, '1/16'));
        const t = trackFor('audio', baseName(it.file.name), { input: { kind: 'browser' } });
        const c: Clip = { id: uid('c'), trackId: t.id, kind: 'audio', name: baseName(it.file.name), start, offset: 0, duration: it.duration, sourceId: it.sourceId, fadeIn: 0.003, fadeOut: 0.005, by };
        p.clips.push(c); newClipIds.push(c.id); firstTrack ??= t.id;
      } else {
        const bar0 = Math.max(0, Math.floor(barAtSec(p, target.time) + 1e-6)), start = secOfBar(p, bar0), b0 = beatAt(p, start);
        for (const part of it.parts) {
          const avg = part.notes.reduce((s, n) => s + n.m, 0) / part.notes.length;
          const instrument = part.drums ? { plugin: 'srs:drum' } : { plugin: 'srs:synth', preset: avg < 48 ? 'bass' : 'keys' };
          const t = trackFor('midi', part.name, { instrument });
          const c: Clip = { id: uid('c'), trackId: t.id, kind: 'midi', name: part.name, start, offset: 0, duration: secOfBar(p, Math.ceil(beatToBar(p, b0 + part.lengthBeats) - 1e-9)) - start, notes: part.notes, fadeIn: 0, fadeOut: 0, by };
          p.clips.push(c); newClipIds.push(c.id); firstTrack ??= t.id;
        }
      }
    }
  });
  useStudio.getState().select(newClipIds, firstTrack);

  // Meldung
  const midiTempo = prepared.find((x): x is Extract<Prepared, { kind: 'midi' }> => x.kind === 'midi' && !!x.bpm && Math.abs(x.bpm - p0.bpm) > 0.5);
  const parts = [`${prepared.length === 1 ? prepared[0].file.name : `${prepared.length} Dateien`} importiert`];
  if (midiTempo) parts.push(`MIDI-Datei hat ${Math.round(midiTempo.bpm! * 10) / 10} BPM, die Noten folgen dem Songtempo ${bpmAt(p0, 0)} BPM`);
  if (errors.length) parts.push(errors.join(' · '));
  if (skipped) parts.push(`${skipped} Datei(en) übersprungen`);
  toast(parts.join('. '));

  // Originaldateien hochladen, damit der Raum sie hört
  const audio = prepared.filter((x): x is Extract<Prepared, { kind: 'audio' }> => x.kind === 'audio');
  if (audio.length && useStudio.getState().token) {
    for (const a of audio) {
      api.uploadFile(a.file, a.file.name, mimeOf(a.file), useStudio.getState().project.id).then((m) => {
        if (!m) return;
        const buf = engine.buffers.get(a.sourceId);
        if (buf) engine.buffers.set(m.id, buf);
        useStudio.getState().replaceSourceId(a.sourceId, m.id);
      }).catch((e) => toast(`${a.file.name}: Hochladen fehlgeschlagen (${e instanceof Error ? e.message : 'Fehler'}). Die Datei klingt nur bei dir.`));
    }
  }
  return newClipIds;
}

const round = (x: number) => Math.round(x * 1000) / 1000;
